import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { formatarDiaISO, formatarReais, STATUS_EVENTO_ROTULO, type StatusEvento } from "@caixaflow/shared";
import { api, ErroApi } from "../../api/cliente";
import type { Evento } from "../../api/tipos";
import { Campo } from "../../components/Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "../../components/Estados";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";

const CLASSE_STATUS: Record<StatusEvento, string> = { PLANEJADO: "neutro", EM_ANDAMENTO: "ok", ENCERRADO: "" };

export function PaginaEventos() {
  const eventos = useQuery({ queryKey: ["eventos"], queryFn: () => api<Evento[]>("/eventos") });
  const [editando, setEditando] = useState<Evento | "novo" | null>(null);
  const [encerrando, setEncerrando] = useState<Evento | null>(null);
  const [excluindo, setExcluindo] = useState<Evento | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const alterar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Evento> }) => api<Evento>(`/eventos/${id}`, "PATCH", dados),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["eventos"] }),
    onError: (e) => toast(mensagemDeErro(e), "erro"),
  });

  return (
    <>
      <div className="linha">
        <p className="mudo pequeno" style={{ margin: 0 }}>
          O operador só escolhe eventos <b>em andamento</b> ao abrir o caixa. Evento encerrado fica somente leitura e pode
          ser excluído (com o histórico) por um ADMIN.
        </p>
        <button className="btn primario" onClick={() => setEditando("novo")}>
          Novo evento
        </button>
      </div>

      {eventos.isPending && <Carregando />}
      {eventos.error && <FalhaAoCarregar erro={eventos.error} tentarDeNovo={() => eventos.refetch()} />}
      {eventos.data?.length === 0 && <div className="card vazio">Nenhum evento cadastrado.</div>}

      <div className="lista-cartoes">
        {eventos.data?.map((ev) => (
          <article key={ev.id} className="card cartao-item">
            <div className="linha" style={{ alignItems: "start" }}>
              <span className="titulo">{ev.nome}</span>
              <span className={`chip ${CLASSE_STATUS[ev.status]}`}>{STATUS_EVENTO_ROTULO[ev.status]}</span>
            </div>
            <span className="mudo pequeno">
              {formatarDiaISO(ev.dataInicio)}
              {ev.dataFim !== ev.dataInicio && ` a ${formatarDiaISO(ev.dataFim)}`}
              {ev.local && ` · ${ev.local}`}
            </span>
            {ev.permiteEstoqueNegativo && <span className="chip alerta">Venda sem estoque liberada</span>}
            <div className="rodape">
              <Link className="btn pequeno fantasma" to={`/admin/produtos-estoque?aba=estoque&evento=${ev.id}`}>
                Cardápio e estoque
              </Link>
              {ev.status !== "ENCERRADO" && (
                <button className="btn pequeno fantasma" onClick={() => setEditando(ev)}>
                  Editar
                </button>
              )}
              {ev.status === "PLANEJADO" && (
                <button
                  className="btn pequeno primario"
                  disabled={alterar.isPending}
                  onClick={() => alterar.mutate({ id: ev.id, dados: { status: "EM_ANDAMENTO" } })}
                >
                  Iniciar
                </button>
              )}
              {ev.status === "EM_ANDAMENTO" && (
                <button className="btn pequeno perigo-suave" onClick={() => setEncerrando(ev)}>
                  Encerrar
                </button>
              )}
              {ev.status !== "EM_ANDAMENTO" && (
                <button className="link-botao perigo" onClick={() => setExcluindo(ev)}>
                  Excluir
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      <Modal aberto={excluindo !== null} titulo="Excluir evento" onFechar={() => setExcluindo(null)}>
        {excluindo && <FormExcluirEvento evento={excluindo} onPronto={() => setExcluindo(null)} />}
      </Modal>

      <Modal aberto={editando !== null} titulo={editando === "novo" ? "Novo evento" : "Editar evento"} onFechar={() => setEditando(null)}>
        {editando !== null && <FormEvento evento={editando === "novo" ? null : editando} onPronto={() => setEditando(null)} />}
      </Modal>

      <Modal aberto={encerrando !== null} titulo="Encerrar evento" onFechar={() => setEncerrando(null)}>
        {encerrando && (
          <div className="form">
            <p style={{ margin: 0 }}>
              Encerrar <b>{encerrando.nome}</b>? Depois de encerrado, o evento fica somente leitura: não abre caixa, não
              vende e não movimenta estoque. Relatórios e exportações continuam disponíveis.
            </p>
            <div className="modal-acoes">
              <button className="btn fantasma" onClick={() => setEncerrando(null)}>
                Voltar
              </button>
              <button
                className="btn perigo"
                disabled={alterar.isPending}
                onClick={() =>
                  alterar.mutate(
                    { id: encerrando.id, dados: { status: "ENCERRADO" } },
                    { onSuccess: () => (setEncerrando(null), toast("Evento encerrado")) },
                  )
                }
              >
                Encerrar evento
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

type Historico = {
  caixas: number;
  vendas: number;
  vendasCanceladas: number;
  totalVendidoCentavos: number;
  movimentacoes: number;
  temHistorico: boolean;
};

/**
 * Exclusão de evento planejado ou encerrado. Sem histórico, basta confirmar. Com histórico (caixas,
 * vendas, estoque), mostra o que será apagado, oferece baixar o relatório antes e pede o nome do
 * evento e a senha do ADMIN — a API confere tudo de novo.
 */
function FormExcluirEvento({ evento, onPronto }: { evento: Evento; onPronto: () => void }) {
  const historico = useQuery({ queryKey: ["evento-historico", evento.id], queryFn: () => api<Historico>(`/eventos/${evento.id}/historico`) });
  const [nome, setNome] = useState("");
  const [senha, setSenha] = useState("");
  const qc = useQueryClient();
  const toast = useToast();
  const excluir = useMutation({
    mutationFn: () => api(`/eventos/${evento.id}/excluir`, "POST", historico.data?.temHistorico ? { confirmacaoNome: nome, senha } : {}),
    onSuccess: () => {
      toast(`Evento "${evento.nome}" excluído`);
      qc.invalidateQueries({ queryKey: ["eventos"] });
      onPronto();
    },
    onError: () => setSenha(""),
  });

  if (historico.isPending) return <Carregando />;
  if (historico.error) return <FalhaAoCarregar erro={historico.error} tentarDeNovo={() => historico.refetch()} />;
  const h = historico.data;
  const nomeConfere = nome.trim().toLocaleLowerCase("pt-BR") === evento.nome.trim().toLocaleLowerCase("pt-BR");
  const filtroRelatorio = `evento=${evento.id}&status=TODAS`;

  return (
    <form
      className="form"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        excluir.mutate();
      }}
    >
      {!h.temHistorico ? (
        <p style={{ margin: 0 }}>
          Excluir <b>{evento.nome}</b>? O cardápio do evento também é removido; os produtos continuam no catálogo.
        </p>
      ) : (
        <>
          <div className="aviso erro">
            <b>Isto apaga definitivamente</b> o histórico de <b>{evento.nome}</b>:
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              <li>
                {h.vendas} venda(s) válida(s), somando {formatarReais(h.totalVendidoCentavos)}
                {h.vendasCanceladas > 0 && ` (mais ${h.vendasCanceladas} cancelada${h.vendasCanceladas > 1 ? "s" : ""})`}
              </li>
              <li>{h.caixas} caixa(s), com os fechamentos</li>
              <li>{h.movimentacoes} movimentação(ões) de estoque</li>
            </ul>
            Não dá para desfazer. Os produtos continuam no catálogo.
          </div>
          <div className="linha-esq">
            <span className="mudo pequeno">Antes, guarde o relatório:</span>
            <a className="btn pequeno fantasma" href={`/api/relatorios/vendas.csv?${filtroRelatorio}`} download>
              Planilha (CSV)
            </a>
            <a className="btn pequeno fantasma" href={`/api/relatorios/relatorio.pdf?${filtroRelatorio}`} download>
              PDF
            </a>
          </div>
          <Campo rotulo="Digite o nome do evento para confirmar">
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder={evento.nome} autoComplete="off" />
          </Campo>
          <Campo rotulo="Sua senha de ADMIN">
            <input type="password" required value={senha} onChange={(e) => setSenha(e.target.value)} autoComplete="new-password" />
          </Campo>
        </>
      )}
      {excluir.error && <div className="aviso erro">{mensagemDeErro(excluir.error)}</div>}
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Voltar
        </button>
        <button type="submit" className="btn perigo" disabled={excluir.isPending || (h.temHistorico && (!nomeConfere || !senha))}>
          {excluir.isPending ? "Excluindo…" : h.temHistorico ? "Excluir definitivamente" : "Excluir evento"}
        </button>
      </div>
    </form>
  );
}

function FormEvento({ evento, onPronto }: { evento: Evento | null; onPronto: () => void }) {
  const [nome, setNome] = useState(evento?.nome ?? "");
  const [dataInicio, setDataInicio] = useState(evento?.dataInicio ?? "");
  const [dataFim, setDataFim] = useState(evento?.dataFim ?? "");
  const [local, setLocal] = useState(evento?.local ?? "");
  const [status, setStatus] = useState<StatusEvento>(evento?.status ?? "PLANEJADO");
  const [permiteEstoqueNegativo, setPermite] = useState(evento?.permiteEstoqueNegativo ?? false);
  const qc = useQueryClient();
  const toast = useToast();

  const salvar = useMutation({
    mutationFn: () => {
      const dados = { nome, dataInicio, dataFim: dataFim || dataInicio, local: local.trim() || null, permiteEstoqueNegativo };
      return evento
        ? api<Evento>(`/eventos/${evento.id}`, "PATCH", dados)
        : api<Evento>("/eventos", "POST", { ...dados, status });
    },
    onSuccess: () => {
      toast(evento ? "Evento atualizado" : "Evento criado");
      qc.invalidateQueries({ queryKey: ["eventos"] });
      onPronto();
    },
  });
  const erro = salvar.error instanceof ErroApi ? salvar.error : null;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    salvar.mutate();
  };

  return (
    <form className="form" onSubmit={enviar}>
      {salvar.error && !erro?.campos.length && <div className="aviso erro">{mensagemDeErro(salvar.error)}</div>}
      <Campo rotulo="Nome" erro={erro?.doCampo("nome")}>
        <input required value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Overflow Conference 26" />
      </Campo>
      <div className="duas-colunas">
        <Campo rotulo="Início" erro={erro?.doCampo("dataInicio")}>
          <input type="date" required value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </Campo>
        <Campo rotulo="Fim" erro={erro?.doCampo("dataFim")}>
          <input type="date" value={dataFim} min={dataInicio} onChange={(e) => setDataFim(e.target.value)} />
        </Campo>
      </div>
      <Campo rotulo="Local" erro={erro?.doCampo("local")}>
        <input value={local} onChange={(e) => setLocal(e.target.value)} />
      </Campo>
      {!evento && (
        <Campo rotulo="Situação">
          <select value={status} onChange={(e) => setStatus(e.target.value as StatusEvento)}>
            <option value="PLANEJADO">Planejado</option>
            <option value="EM_ANDAMENTO">Em andamento (já pode abrir caixa)</option>
          </select>
        </Campo>
      )}
      <label className="marcar">
        <input type="checkbox" checked={permiteEstoqueNegativo} onChange={(e) => setPermite(e.target.checked)} />
        <span>
          Permitir venda sem saldo em estoque
          <br />
          <span className="mudo pequeno">O saldo pode ficar negativo neste evento. Use só se o estoque não for contado.</span>
        </span>
      </label>
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Cancelar
        </button>
        <button className="btn primario" type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </form>
  );
}
